import React, { useState, useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Card from '@amzn/meridian/card';
import Box from '@amzn/meridian/box';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import plusKnockoutTokens from '@amzn/meridian-tokens/base/icon/plus-knockout';
import minusKnockoutTokens from '@amzn/meridian-tokens/base/icon/minus-knockout';
import closeKnockoutTokens from '@amzn/meridian-tokens/base/icon/close-knockout';
import warning from '@amzn/meridian-tokens/base/icon/alert-warning-small';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import { WARNING_COLOR, ERROR_COLOR, SUCCESS_COLOR, BLUE_COLOR } from '@constants/values';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { AgileRecommendationTypes, AgileRecommendationInput } from '@components/agile/types/AgileRecommendationTypes';
import useGetSwapRecommendationsResults from '@store/hooks/useGetSwapRecommendationsResults';
import useSwapAssortment from '@store/hooks/useSwapAssortment';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import AssortmentFacingUpdates from '@components/agile/types/AssortmentFacingUpdates';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import { useNavigate } from 'react-router-dom';
import { AsinMetadataEntry } from '@graphql/generated/graphql';
import { CellValueChangedEvent } from 'ag-grid-community';
import getDateInUTC from '@components/agile/utils/GetDateUtils';
import { getConcatnatedSubcategoryName } from '@components/agile/utils/FixtureDataUtils';
import {
  colDefs,
  calculateSum,
  calculateUnitsToRemove,
  CalculateNewOccupiedFacings,
  mergeDuplicateProductChanges,
} from '@components/agile/utils/RecommendationTableUtils';
import Column from '@amzn/meridian/column';
import Tooltip from '@amzn/meridian/tooltip';
import ColumnSelector from '@components/shared/ColumnSelector';
import { useColumnSelector } from 'src/hooks/useColumnSelector';

export interface AtRecommendationTableProps {
  recommendationInput: AgileRecommendationInput[];
  zoneDetails: AgileMacroplanogram | undefined;
  swapTime: number;
  newAsinMetaData: AsinMetadataEntry[] | undefined | null;
  totalNewFacings: number;
}

const AtAddRecommendationTable = (props: AtRecommendationTableProps) => {
  const navigate = useNavigate();
  const [rowData, setRowData] = useState<AgileRecommendationTypes[]>([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [successful, setSuccessful] = useState(false);
  const [swapErrorMsg, setSwapErrorMsg] = useState<string | undefined>(undefined);
  const [disableSwapButton, setDisableSwapButton] = useState<boolean>(true);
  const [recommenationFalg, setRecommendationFlag] = useState<boolean>(true);
  const [enableLoader, setLoader] = useState(true);
  const [totalRemovedFacings, setTotalRemovedFacings] = useState(0);
  const [facingListToUpdate, setFacingListToUpdate] = useState<AgileRecommendationTypes[] | undefined>(undefined);
  const [facingUnitsToRemoveList, setfacingUnitsToRemove] = useState<AssortmentFacingUpdates[]>([]);
  const { recommendationInput, zoneDetails, swapTime, newAsinMetaData, totalNewFacings } = props;
  const [newOccupiedFacings, setNewOccupiedFacings] = useState(totalNewFacings);
  const [unitsToRemove, setUnitsToRemove] = useState(totalNewFacings);
  const [newAssortment, setNewAssortment] = useState(recommendationInput);
  const { mutateAsync: swapAssortment } = useSwapAssortment();

  const defaultVisibleColumns = [
    'asin',
    'title',
    'image',
    'availableFacings',
    'facingToRemove',
    'remainingFacings',
    'difference',
    'shrinkPerOps',
    'shrinkPerOps13week',
    'shrinkPerOps26week',
    'movingAvgSales13week',
    'movingAvgSales52week',
  ];

  const { visibleColumns, columnDefs, toggleColumn } = useColumnSelector({
    allColumns: colDefs,
    defaultVisibleColumns,
    storageKey: 'addRecommendationColumns',
  });

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
    }),
    [],
  );

  const response = useGetSwapRecommendationsResults({
    storeId: zoneDetails?.storeId ?? '',
    macroplanogramId: zoneDetails?.entryId ?? '',
    swapTime,
    endTime: zoneDetails?.endTime,
    addAssortment: [],
    includeDraft: false,
  });

  // Calculate zone-specific capacity from filtered subcategories
  // For zones with shared fixtures, use unique fixture count
  const zoneMaxFacingUnits = useMemo(() => {
    if (zoneDetails?.storeSubcategories && zoneDetails.storeSubcategories.length > 0) {
      // Check if we have zone-specific data
      const hasZoneData = zoneDetails.storeSubcategories.some(
        (sc) => sc.zoneFixtureIds && sc.zoneFixtureIds.length > 0,
      );

      if (hasZoneData) {
        // Aggregate model: use the selected zone's maxFacingUnits
        // Note: zoneDetails is pre-filtered by AtAgileHeader to contain only the selected zone
        return Number(zoneDetails.storeSubcategories[0].maxFacingUnits) || response.maxFacingUnits;
      }
    }
    // Fallback to macroplanogram level
    return response.maxFacingUnits;
  }, [zoneDetails, response.maxFacingUnits]);

  // Get the selected zone's subcategory ID for filtering
  const selectedSubcategoryId = useMemo(() => {
    if (zoneDetails?.storeSubcategories && zoneDetails.storeSubcategories.length > 0) {
      const hasZoneData = zoneDetails.storeSubcategories.some(
        (sc) => sc.zoneFixtureIds && sc.zoneFixtureIds.length > 0,
      );
      if (hasZoneData) {
        // In aggregate model, zoneDetails contains only the selected zone
        return zoneDetails.storeSubcategories[0].subcategoryId;
      }
    }
    return null;
  }, [zoneDetails]);

  // Filter results by subcategory if in aggregate model
  const filteredResults = useMemo(() => {
    if (selectedSubcategoryId && response.results) {
      return response.results.filter((asin) => asin.subcategoryId === selectedSubcategoryId);
    }
    return response.results || [];
  }, [response.results, selectedSubcategoryId]);

  // Calculate occupied facings for the selected subcategory only
  const subcategoryOccupiedFacings = useMemo(() => {
    if (selectedSubcategoryId && response.results) {
      return response.results
        .filter((asin) => asin.subcategoryId === selectedSubcategoryId)
        .reduce((sum, asin) => sum + asin.availableFacings, 0);
    }
    return response.occupiedFacingUnits;
  }, [response.results, response.occupiedFacingUnits, selectedSubcategoryId]);

  useEffect(() => {
    const tempRowData: AgileRecommendationTypes[] = [];
    if (filteredResults === undefined) {
      setLoader(true);
    } else {
      setLoader(false);
    }
    // eslint-disable-next-line max-len
    const unitsToRemoveResult = calculateUnitsToRemove(
      totalNewFacings,
      zoneMaxFacingUnits,
      subcategoryOccupiedFacings,
      totalRemovedFacings,
    );
    if (unitsToRemoveResult === 0) {
      setDisableSwapButton(false);
      setRecommendationFlag(false);
    } else {
      setDisableSwapButton(true);
      setRecommendationFlag(true);
    }

    filteredResults.forEach((rawRow) => {
      const rowObj = {
        asin: rawRow.asin,
        availableFacings: rawRow.availableFacings,
        facingToRemove: rawRow.facingToRemove,
        entryId: rawRow.entryId,
        fohCapacity: rawRow.fohCapacity,
        uphl1: rawRow.uphl1,
        uphl2: rawRow.uphl2,
        uphl3: rawRow.uphl3,
        title: rawRow.title,
        image: rawRow.image,
        remainingFacings: rawRow.remainingFacings,
        remainingCapacity: rawRow.remainingCapacity,
        difference: rawRow.difference,
        shrinkCost: rawRow.shrinkCost,
        shrinkPerOps: rawRow.shrinkPerOps,
        shrinkPerOps13week: rawRow.shrinkPerOps13week,
        shrinkPerOps26week: rawRow.shrinkPerOps26week,
        shrinkPerOps52week: rawRow.shrinkPerOps52week,
        movingAvgSales13week: rawRow.movingAvgSales13week,
        movingAvgSales26week: rawRow.movingAvgSales26week,
        movingAvgSales52week: rawRow.movingAvgSales52week,
      } as AgileRecommendationTypes;
      tempRowData.push(rowObj);
    });
    setRowData(tempRowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredResults]);

  useEffect(() => {
    // eslint-disable-next-line max-len
    const facingsToRemove = calculateUnitsToRemove(
      totalNewFacings,
      zoneMaxFacingUnits,
      subcategoryOccupiedFacings,
      totalRemovedFacings,
    );
    const calculatedOccupiedFacings = CalculateNewOccupiedFacings(
      totalNewFacings,
      subcategoryOccupiedFacings,
      totalRemovedFacings,
    );
    setNewOccupiedFacings(calculatedOccupiedFacings);
    if (facingsToRemove === 0 && calculatedOccupiedFacings === zoneMaxFacingUnits) {
      setUnitsToRemove(0);
      setDisableSwapButton(false);
    } else {
      setUnitsToRemove(facingsToRemove);
      setDisableSwapButton(true);
    }
  }, [totalNewFacings, zoneMaxFacingUnits, subcategoryOccupiedFacings, totalRemovedFacings]);

  const onSwapAssortment = async () => {
    await swapAssortment({
      input: {
        storeId: zoneDetails?.storeId ? zoneDetails.storeId : '',
        macroplanogramId: zoneDetails?.entryId ? zoneDetails.entryId : '',
        swapTime,
        endTime: zoneDetails?.endTime,
        addAssortment: newAssortment,
        assortmentFacingUpdates: facingUnitsToRemoveList,
        isDraft: false,
      },
    }).then((resp) => {
      // eslint-disable-next-line promise/always-return
      if (resp.swapAssortment.error === null) {
        setOpenConfirmationModal(false);
        setSuccessful(true);
      } else {
        setSwapErrorMsg(resp.swapAssortment.error?.errorMessage);
      }
    });
  };

  const onClick = () => {
    const { uniqueRecommendedAsinList, uniqueNewAsinList } = mergeDuplicateProductChanges(recommendationInput, rowData);
    setNewAssortment(uniqueNewAsinList);
    const filterFacingUpdatesList = uniqueRecommendedAsinList?.filter(
      (data) =>
        +data.difference !== +0 ||
        Math.trunc((data.fohCapacity / data.availableFacings) * data.remainingFacings - data.remainingCapacity) !== 0,
    );
    setFacingListToUpdate(filterFacingUpdatesList);
    const facingUpdatesList = filterFacingUpdatesList?.map<AssortmentFacingUpdates>((data) => ({
      entryId: data.entryId,
      facingUnits: parseInt(String(data.remainingFacings), 10),
      fohCapacity: data.remainingCapacity,
    }));
    setfacingUnitsToRemove(facingUpdatesList);
    if (!disableSwapButton) {
      setOpenConfirmationModal(true);
    } else {
      setOpenConfirmationModal(false);
    }
  };
  const onClose = () => {
    const date = getDateInUTC(swapTime);
    setOpenConfirmationModal(false);
    setSuccessful(false);
    navigate('/assortmentSearch', {
      state: {
        storeId: zoneDetails?.storeId,
        date,
        zoneId: zoneDetails?.entryId,
      } as AgileURLParameterTypes,
    });
  };
  const onCancelSwapAssortment = () => {
    setOpenConfirmationModal(false);
    setSuccessful(false);
  };

  function getChangeStatusForFacings(difference: number) {
    if (difference < 0) {
      return (
        <Text className={BLUE_COLOR}>
          <TableCell alignmentHorizontal="center">
            <Icon tokens={minusKnockoutTokens} />
          </TableCell>
        </Text>
      );
    }
    if (difference === 0) {
      return (
        <Text className={SUCCESS_COLOR}>
          <TableCell alignmentHorizontal="center">
            <Icon tokens={copyTokens} />
          </TableCell>
        </Text>
      );
    }
    return (
      <Text className={SUCCESS_COLOR}>
        <TableCell alignmentHorizontal="center">
          <Icon tokens={plusKnockoutTokens} />
        </TableCell>
      </Text>
    );
  }
  function getTotalFohCapacity(data: AgileRecommendationTypes) {
    const isFohCapacityChanged =
      (data.fohCapacity / data.availableFacings) * data.remainingFacings === data.remainingCapacity;

    if (isFohCapacityChanged) {
      return <Text>{data.remainingCapacity}</Text>;
    }
    return (
      <Row alignmentHorizontal="center">
        <Tooltip position="end" title="FoH capacity per facing is changed" id="myTooltip">
          <Icon className={WARNING_COLOR} tokens={warning} />
        </Tooltip>

        <Text>{data.remainingCapacity}</Text>
      </Row>
    );
  }

  /* On changing facing units to remove, onCellValueChanged function will
     update row data and will enable swap button if unitsToRemove
      is equal to total input facing units.
   */
  const onCellValueChangedHandler = (event: CellValueChangedEvent<AgileRecommendationTypes>) => {
    if (event.rowIndex != null) {
      const newData = [...rowData];
      const rowIndex = newData.findIndex((item) => item.asin === event.data.asin);
      newData[rowIndex].difference = event.data.remainingFacings - event.data.availableFacings;
      newData[rowIndex].remainingCapacity = Math.round(
        (event.data.fohCapacity / event.data.availableFacings) * event.data.remainingFacings,
      );
      newData[rowIndex] = event.data;
      setRowData(newData);
      const removedFacingsSum = calculateSum(rowData, 'difference');
      setTotalRemovedFacings(removedFacingsSum);
      const unitstoRemove = calculateUnitsToRemove(
        totalNewFacings,
        zoneMaxFacingUnits,
        subcategoryOccupiedFacings,
        removedFacingsSum,
      );
      setUnitsToRemove(unitstoRemove);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const notEmpty = (...arrays: (any[] | undefined)[]) => arrays.some((array) => array && array.length > 0);

  return enableLoader ? (
    <Column alignmentHorizontal="center" alignmentVertical="center" spacing="800" spacingInset="500">
      <Loader type="circular" size="large" />{' '}
    </Column>
  ) : (
    <>
      <Row spacing="400" alignmentHorizontal="justify" spacingInset="400">
        <Text type="h300">
          {`Adjust existing capacity of products in the ${zoneDetails ? getConcatnatedSubcategoryName(zoneDetails) : 'Unknown'} zone.`}
          <Text type="b100">
            Ensure the added ASIN(s) will fit into the capacity of the zone. Reduce/remove facings of existing products
            until the zone is completely full.
          </Text>
        </Text>
        <Box type="outline" spacingInset="400">
          <Text type="h200">{`Existing Facings: ${subcategoryOccupiedFacings}/${zoneMaxFacingUnits}`}</Text>
          <Text type="h200">{`New Total Facings: ${newOccupiedFacings}/${zoneMaxFacingUnits}`}</Text>
          {newOccupiedFacings < zoneMaxFacingUnits && (
            <Alert type="warning" size="small">
              <Text type="h200">Zone is not full</Text>
            </Alert>
          )}
          {unitsToRemove > 0 && (
            <Alert type="error" size="small">
              <Text type="h200">{`Facings to remove: ${unitsToRemove}`}</Text>
            </Alert>
          )}
        </Box>
      </Row>
      {recommenationFalg ? (
        <>
          <Row spacing="400" alignmentHorizontal="end" spacingInset="400">
            <ColumnSelector allColumns={colDefs} visibleColumns={visibleColumns} onToggleColumn={toggleColumn} />
          </Row>
          <Row spacing="400" alignmentHorizontal="center" spacingInset="400">
            <div style={{ height: 'auto', width: 2000 }} className="ag-theme-alpine">
              {rowData && (
                <AgGridReact<AgileRecommendationTypes>
                  rowData={rowData}
                  domLayout="autoHeight"
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowSelection="multiple"
                  enableCellTextSelection
                  onCellValueChanged={onCellValueChangedHandler}
                />
              )}
            </div>
          </Row>
        </>
      ) : (
        <>
          <Text type="h300" alignment="center">
            Nothing to remove.
          </Text>
          <Row alignmentHorizontal="center">
            <Button onClick={() => setRecommendationFlag(true)} type="link" size="large">
              Show Existing Assortment Anyway.
            </Button>
          </Row>
        </>
      )}
      {disableSwapButton && (
        <Alert size="small" type="error">
          <Text>Zone is not full or has more facings than allowed.</Text>
        </Alert>
      )}
      <Row alignmentHorizontal="center" spacingInset="600" spacing="400" backgroundColor="subdued">
        <Button onClick={onClick} disabled={disableSwapButton} data-testid="DisableSwapButton">
          Review changes
        </Button>
        <Modal open={openConfirmationModal} scrollContainer="viewport" width="500" aria-describedby="modal-description">
          <Alert size="large" type="warning">
            Please review and confirm the changes.
          </Alert>
          <Row alignmentHorizontal="center" spacingInset="300" spacing="400">
            <Card>
              <Table headerRows={1} spacing="small">
                <TableRow>
                  <TableCell alignmentHorizontal="center">ASIN</TableCell>
                  <TableCell alignmentHorizontal="center">Title</TableCell>
                  <TableCell alignmentHorizontal="center">Change</TableCell>
                  <TableCell alignmentHorizontal="center">Diff</TableCell>
                  <TableCell alignmentHorizontal="center">Live Date</TableCell>
                  <TableCell alignmentHorizontal="center">UPHL2</TableCell>
                  <TableCell alignmentHorizontal="center">#New Total Facings</TableCell>
                  <TableCell alignmentHorizontal="center">New Total FOH Capacity</TableCell>
                </TableRow>
                {newAssortment.map((data) => (
                  <TableRow>
                    <TableCell alignmentHorizontal="center">
                      <Text>{data.asin}</Text>
                    </TableCell>
                    <TableCell alignmentHorizontal="center">
                      <Text>{newAsinMetaData?.find((asin) => asin.asin === data.asin)?.title ?? 'Unknown'}</Text>
                    </TableCell>
                    <TableCell alignmentHorizontal="center">
                      <Text className={SUCCESS_COLOR}>
                        <TableCell alignmentHorizontal="center">
                          <Icon tokens={plusKnockoutTokens} />
                        </TableCell>
                      </Text>
                    </TableCell>
                    <TableCell alignmentHorizontal="center">
                      <Text>{data.facingUnits}</Text>
                    </TableCell>
                    <TableCell alignmentHorizontal="center">
                      <Text>{getDateInUTC(swapTime)}</Text>
                    </TableCell>
                    <TableCell alignmentHorizontal="center">
                      {newAsinMetaData?.find((asin) => asin.asin === data.asin)?.uph?.uphl2 ?? 'Unknown'}
                    </TableCell>
                    <TableCell alignmentHorizontal="center">
                      <Text>NewAsin</Text>
                    </TableCell>
                    <TableCell alignmentHorizontal="center">
                      <Text>{data.fohCapacity}</Text>
                    </TableCell>
                  </TableRow>
                ))}
                {facingListToUpdate &&
                  facingListToUpdate.map((data) => (
                    <TableRow>
                      <TableCell alignmentHorizontal="center">{data.asin}</TableCell>
                      <TableCell alignmentHorizontal="center">{data.title}</TableCell>
                      {+data.remainingFacings === 0 ? (
                        <Text className={ERROR_COLOR}>
                          <TableCell alignmentHorizontal="center">
                            <Icon tokens={closeKnockoutTokens} />
                          </TableCell>
                        </Text>
                      ) : (
                        getChangeStatusForFacings(data.difference)
                      )}
                      <TableCell alignmentHorizontal="center">{data.difference}</TableCell>
                      <TableCell alignmentHorizontal="center">
                        <Text>{getDateInUTC(swapTime)}</Text>
                      </TableCell>
                      <TableCell alignmentHorizontal="center">{data.uphl2}</TableCell>
                      <TableCell alignmentHorizontal="center">
                        <Text>{data.remainingFacings}</Text>
                      </TableCell>
                      <TableCell alignmentHorizontal="center">{getTotalFohCapacity(data)}</TableCell>
                    </TableRow>
                  ))}
              </Table>
            </Card>
          </Row>
          {!notEmpty(newAssortment, facingListToUpdate) && (
            <Row alignmentHorizontal="center">
              <Alert size="small" type="error">
                {' '}
                No change to submit
              </Alert>
            </Row>
          )}
          <ModalFooter>
            <Row alignmentHorizontal="center" widths="fit">
              <Button type="secondary" size="large" onClick={onCancelSwapAssortment} data-testid="CancelSwapButton">
                Cancel
              </Button>
              {notEmpty(newAssortment, facingListToUpdate) && (
                <Button type="primary" size="large" onClick={onSwapAssortment} data-testid="SwapButton">
                  Submit
                </Button>
              )}
            </Row>
          </ModalFooter>
        </Modal>
        {swapErrorMsg && (
          <Modal
            open={openConfirmationModal}
            scrollContainer="viewport"
            aria-describedby="modal-description"
            onClose={() => {
              setSwapErrorMsg(undefined);
              setOpenConfirmationModal(false);
            }}
          >
            <Alert size="xlarge" type="error">
              <h2>Error Message!</h2>
            </Alert>
            <Text>{swapErrorMsg}</Text>
            <Row alignmentHorizontal="center" widths="fit" />
          </Modal>
        )}
        {successful ? (
          <Modal open={successful} scrollContainer="viewport" aria-describedby="modal-description" onClose={onClose}>
            <Alert type="success" size="xlarge">
              ASINs Swapped Successfully
            </Alert>
          </Modal>
        ) : null}
      </Row>
    </>
  );
};
export default React.memo(AtAddRecommendationTable);
